<template>
  <div class="lead-page">
    <header class="lead-header">
      <router-link to="/" class="lead-brand" aria-label="PickPack, volver al inicio">
        <span class="brand-mark" aria-hidden="true"><span></span><span></span></span>
        <span>PickPack</span>
      </router-link>
      <router-link to="/" class="back-link">
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M16 10H5m4-4-4 4 4 4" /></svg>
        Volver
      </router-link>
    </header>

    <main class="lead-shell">
      <section class="lead-intro">
        <p class="eyebrow"><span></span> Hablemos de tu operación</p>
        <h1>{{ isCollectionsLead ? "Hagamos tus entregas más simples." : "Queremos conocer tu negocio." }}</h1>
        <p>
          {{ isCollectionsLead
            ? "Cuéntanos cómo preparas tus pedidos. Te contactaremos para explicarte cómo funcionan las Recolecciones PickPack."
            : "Te contactaremos para entender tu operación y cómo puede ayudarte PickPack."
          }}
        </p>
      </section>

      <section class="lead-card">
        <template v-if="!submitted">
          <div class="form-heading"><h2>Déjanos tus datos</h2></div>

          <form @submit.prevent="sendLead">
            <div class="form-grid">
              <div class="form-field">
                <label for="lead-name">Nombre</label>
                <input id="lead-name" v-model.trim="lead.name" type="text" autocomplete="name" maxlength="120" placeholder="Nombre y apellido" required />
              </div>

              <div class="form-field">
                <label for="lead-business">Nombre del negocio</label>
                <input id="lead-business" v-model.trim="lead.business_name" type="text" autocomplete="organization" maxlength="160" placeholder="Tu negocio" required />
              </div>

              <div class="form-field full-field">
                <label for="lead-phone">WhatsApp</label>
                <div class="phone-field">
                  <select v-model="lead.whatsapp_country_code" aria-label="Código de país">
                    <option v-for="country in countries" :key="country.code" :value="country.code">
                      {{ country.label }} {{ country.code }}
                    </option>
                  </select>
                  <input id="lead-phone" v-model.trim="lead.whatsapp_number" type="tel" inputmode="tel" autocomplete="tel-national" maxlength="20" placeholder="55 1234 5678" required />
                </div>
              </div>

              <div class="form-field full-field">
                <label for="lead-link">Sitio o Instagram <span>Opcional</span></label>
                <input id="lead-link" v-model.trim="lead.business_link" type="text" inputmode="url" maxlength="500" placeholder="sitio.com o @tuempresa" />
              </div>
            </div>

            <button type="submit" :disabled="submitting">
              {{ submitting ? "Enviando…" : isCollectionsLead ? "Conocer Recolecciones" : "Quiero conocer PickPack" }}
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11m-4-4 4 4-4 4" /></svg>
            </button>
          </form>
        </template>

        <div v-else class="success-state">
          <span>✓</span>
          <p>Datos recibidos</p>
          <h2>Gracias, {{ lead.name }}.</h2>
          <p>Te contactaremos para conocer más sobre {{ lead.business_name }}.</p>
          <router-link to="/">Volver a PickPack</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";

export default {
  name: "LeadCapture",
  setup() {
    const route = useRoute();
    const submitting = ref(false);
    const submitted = ref(false);
    const lead = ref({
      name: "",
      whatsapp_country_code: "+52",
      whatsapp_number: "",
      business_name: "",
      business_link: "",
    });
    const countries = [
      { label: "México", code: "+52" },
      { label: "EE. UU. / Canadá", code: "+1" },
      { label: "Guatemala", code: "+502" },
      { label: "Colombia", code: "+57" },
      { label: "Argentina", code: "+54" },
      { label: "Chile", code: "+56" },
      { label: "Perú", code: "+51" },
      { label: "España", code: "+34" },
    ];
    const isCollectionsLead = computed(() => route.query.interes === "recolecciones");

    async function sendLead() {
      if (submitting.value || submitted.value) return;
      submitting.value = true;
      try {
        await axios.post("/api/leads", lead.value);
        submitted.value = true;
        useToast().success("Gracias. Pronto estaremos en contacto.", { position: "top-right" });
      } catch (error) {
        useToast().error(
          error.response?.data?.error || "No pudimos enviar tus datos. Inténtalo nuevamente.",
          { position: "top-right" }
        );
      } finally {
        submitting.value = false;
      }
    }

    return { countries, isCollectionsLead, lead, sendLead, submitted, submitting };
  },
};
</script>

<style scoped>
.lead-page{--ink:#17332b;--deep:#0d241e;--green:#428b74;--bright:#73b99e;--paper:#fffdf8;--orange:#ee8b46;height:100%;overflow-y:auto;background:var(--paper);color:var(--ink);font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.lead-page *,.lead-page *:before,.lead-page *:after{box-sizing:border-box}.lead-header{width:min(1180px,calc(100% - 48px));height:80px;display:flex;align-items:center;justify-content:space-between;margin:0 auto;border-bottom:1px solid rgba(23,51,43,.14)}.lead-brand{display:inline-flex;align-items:center;gap:11px;color:var(--ink);font-size:22px;font-weight:800;letter-spacing:-.04em;text-decoration:none}.brand-mark{position:relative;width:27px;height:27px}.brand-mark span{position:absolute;width:14px;height:14px;border:2px solid var(--green);border-radius:3px}.brand-mark span:first-child{top:1px;left:1px}.brand-mark span:last-child{right:1px;bottom:1px;background:var(--green)}.back-link{display:inline-flex;align-items:center;gap:7px;color:#53645e;font-size:13px;font-weight:700;text-decoration:none}.back-link svg{width:20px;fill:none;stroke:currentColor;stroke-width:1.7}.lead-shell{width:min(1180px,calc(100% - 48px));min-height:calc(100% - 80px);display:grid;grid-template-columns:.92fr 1.08fr;align-items:center;gap:100px;margin:0 auto;padding:70px 0}.eyebrow{display:flex;align-items:center;gap:10px;margin:0 0 24px;color:var(--green);font-size:11px;font-weight:800;letter-spacing:.14em;text-transform:uppercase}.eyebrow span{width:24px;height:2px;background:currentColor}.lead-intro h1{max-width:580px;margin:0;font-size:clamp(50px,5.5vw,75px);line-height:.98;letter-spacing:-.06em}.lead-intro>p:not(.eyebrow){max-width:550px;margin:28px 0 0;color:#61716b;font-size:17px;line-height:1.7}.lead-card{padding:44px;border:1px solid rgba(23,51,43,.1);border-radius:12px;background:#fff;color:var(--ink);box-shadow:0 24px 70px rgba(23,51,43,.12)}.form-heading{padding-bottom:28px;border-bottom:1px solid rgba(23,51,43,.14)}.form-heading h2{margin:0;font-size:25px;letter-spacing:-.035em}.lead-card form{margin-top:29px}.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:19px 16px}.form-field{min-width:0}.full-field{grid-column:1/-1}.form-field label{display:block;margin-bottom:8px;color:#53645e;font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.form-field label span{color:#9aa5a1;font-weight:600}.form-field input,.form-field select{width:100%;min-width:0;height:52px;padding:0 14px;border:1px solid #cfd7d3;border-radius:5px;outline:none;background:#fff;color:var(--ink);font-size:14px}.form-field input::placeholder{color:#a3ada9}.form-field input:focus,.form-field select:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(66,139,116,.12)}.phone-field{display:grid;grid-template-columns:180px 1fr;gap:9px}.lead-card form>button{width:100%;height:54px;display:flex;align-items:center;justify-content:center;gap:8px;margin-top:24px;border:0;border-radius:5px;background:var(--ink);color:#fff;font-size:14px;font-weight:800;cursor:pointer}.lead-card form>button:hover{background:var(--green)}.lead-card form>button:disabled{cursor:default;opacity:.65}.lead-card form>button svg{width:20px;fill:none;stroke:currentColor;stroke-width:1.7}.success-state{min-height:500px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}.success-state>span{width:58px;height:58px;display:grid;place-items:center;border-radius:50%;background:#dcece6;color:var(--green);font-size:24px;font-weight:900}.success-state>p:first-of-type{margin:22px 0 7px;color:var(--green);font-size:10px;font-weight:800;letter-spacing:.12em;text-transform:uppercase}.success-state h2{margin:0;font-size:36px;letter-spacing:-.045em}.success-state>p:last-of-type{max-width:390px;margin:15px 0 28px;color:#697873;line-height:1.65}.success-state a{color:var(--green);font-size:13px;font-weight:800}
@media(max-width:850px){.lead-shell{grid-template-columns:1fr;gap:50px;padding:60px 0 80px}.lead-intro h1{max-width:700px}.lead-card{min-height:auto}.success-state{min-height:400px}}
@media(max-width:560px){.lead-header,.lead-shell{width:calc(100% - 32px)}.lead-header{height:68px}.lead-shell{padding:48px 0 60px}.lead-intro h1{font-size:46px}.lead-intro>p:not(.eyebrow){font-size:15px}.response-note{margin-top:30px}.lead-card{padding:28px 20px}.form-grid{grid-template-columns:1fr}.full-field{grid-column:auto}.phone-field{grid-template-columns:145px 1fr}.form-heading h2{font-size:22px}}
</style>
